import { useEffect, useState, useSyncExternalStore } from "react";
import { ASK_BAR_HEIGHT, ASK_BAR_WIDTH } from "./constants";

/**
 * The resting Ask-Bar's height, for AskBarSlot and AssistantHost alike.
 *
 * ASK_BAR_HEIGHT holds the recording notice at the two lines it takes at
 * ASK_BAR_WIDTH. Narrower, the notice wraps to more lines, and the fixed height
 * clipped them away — at 390px half the statement and both links, which
 * specs/ai-transparency requires whole. Below the resting width the height
 * therefore grows by however much taller the notice actually is.
 *
 * Measured rather than computed, because the line count depends on the text,
 * the language and the font. Until the first measurement — the server render
 * and every paint up to it — `--ask-bar-height` in globals.css supplies the
 * same number from media queries, pre-measured on the German text the server
 * renders. The measurement only corrects it, so hydration does not move the
 * hero.
 */

/** The notice's share of ASK_BAR_HEIGHT, its divider included: two lines. */
const RESTING_NOTICE_HEIGHT = 45;

/**
 * @param width the slot's width, i.e. the resting panel's.
 * @param noticeHeight the notice's measured border-box height at that width.
 */
export function restingHeight(width: number, noticeHeight: number): number {
  if (width >= ASK_BAR_WIDTH) return ASK_BAR_HEIGHT;
  return (
    ASK_BAR_HEIGHT +
    Math.max(0, Math.ceil(noticeHeight) - RESTING_NOTICE_HEIGHT)
  );
}

/*
 * The measurement lives outside React: the notice is rendered by the panel,
 * but the slot that has to reserve its height sits elsewhere in the page.
 */
let noticeHeight: number | null = null;
const listeners = new Set<() => void>();

function subscribe(onChange: () => void) {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

/** The notice's last measured height, or `null` before the first measurement. */
export function useNoticeHeight(): number | null {
  return useSyncExternalStore(
    subscribe,
    () => noticeHeight,
    () => null,
  );
}

/**
 * Measures the notice while `active` and publishes its height; returns the
 * ref to put on it. Active only while the panel sits on the slot: docked, the
 * notice wraps to the docked width, which says nothing about the slot's.
 */
export function useMeasureNotice(active: boolean) {
  const [node, setNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!active || !node) return;
    const ro = new ResizeObserver(([entry]) => {
      const height = entry.borderBoxSize[0].blockSize;
      if (height === noticeHeight) return;
      noticeHeight = height;
      listeners.forEach((onChange) => onChange());
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [active, node]);
  return setNode;
}
